import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  useTheme,
  styled
} from '@mui/material';
import {
  Assignment as DetailsIcon,
  Assessment as FeasibilityIcon,
  Handshake as NegotiationIcon,
  PlayArrow as ExecutionIcon,
  Send as ResultsIcon
} from '@mui/icons-material';

// Benutzerdefinierter StepConnector für die Verbindungslinien
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.divider,
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.success.main,
    },
  }
}));

// Benutzerdefiniertes StepIconComponent
const CustomStepIcon = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active 
    ? theme.palette.primary.main 
    : ownerState.completed 
      ? theme.palette.success.main 
      : theme.palette.grey[100],
  zIndex: 1,
  color: ownerState.active || ownerState.completed 
    ? theme.palette.common.white 
    : theme.palette.text.secondary,
  width: 40,
  height: 40,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: ownerState.active 
    ? '0 4px 10px 0 rgba(0,0,0,0.25)' 
    : ownerState.completed 
      ? '0 4px 10px 0 rgba(0,0,0,0.15)' 
      : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: !ownerState.disabled ? 'translateY(-2px)' : 'none',
    boxShadow: !ownerState.disabled ? '0 6px 12px rgba(0,0,0,0.2)' : 'none',
  }
}));

// StepIcon-Komponente
function StepIconComponent(props) {
  const { active, completed, className, icon, onClick } = props;
  
  const icons = {
    1: <DetailsIcon />,
    2: <FeasibilityIcon />,
    3: <NegotiationIcon />,
    4: <ExecutionIcon />,
    5: <ResultsIcon />,
  };

  return (
    <CustomStepIcon
      ownerState={{ active, completed, disabled: !active && !completed }}
      className={className}
      onClick={onClick}
      style={{ cursor: completed || active ? 'pointer' : 'default' }}
    >
      {icons[String(icon)]}
    </CustomStepIcon>
  );
}

// Benutzerdefiniertes StepLabel
const CustomStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    '&.Mui-active': {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      fontWeight: 500,
      color: theme.palette.success.main,
    },
  },
  '& .MuiStepLabel-iconContainer': {
    paddingRight: 0,
  }
}));

// Hauptkomponente
const ProcessStepper = ({ activeStep = 2, title = "Bearbeitungsstatus der Anfrage", requestStatus, requestId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const steps = [
    'Details',
    'Machbarkeit',
    'Verhandlung',
    'Ausführung',
    'Ergebnisse'
  ];

  // Bestimme den tatsächlichen aktiven Schritt basierend auf dem Status
  const getActiveStepFromStatus = (status) => {
    switch (status) {
      case 'new':
        return 0; // Details
      case 'feasibility':
        return 1; // Machbarkeit
      case 'negotiation':
        return 2; // Verhandlung
      case 'in_progress':
        return 3; // Ausführung
      case 'completed':
      case 'payment_pending':
      case 'delivered':
        return 4; // Ergebnisse
      default:
        return activeStep; // Fallback auf den übergebenen activeStep
    }
  };

  // Wenn ein requestStatus übergeben wurde, verwende diesen zur Bestimmung des aktiven Schritts
  const effectiveActiveStep = requestStatus ? getActiveStepFromStatus(requestStatus) : activeStep;

  // Bestimme, ob alle Schritte als abgeschlossen markiert werden sollen
  // (für den Fall, dass wir uns in einem früheren Schritt einer bereits abgeschlossenen Anfrage befinden)
  const isCompleted = requestStatus && ['completed', 'payment_pending', 'delivered'].includes(requestStatus);
  
  // Funktion zum Navigieren zu einer bestimmten Seite basierend auf dem Schritt
  const handleStepClick = (stepIndex) => {
    if (!requestId) return;
    
    // Nur aktive oder abgeschlossene Schritte sind klickbar
    if (stepIndex > effectiveActiveStep && !isCompleted) return;
    
    switch (stepIndex) {
      case 0: // Details
        navigate(`/incoming-requests?id=${requestId}`);
        break;
      case 1: // Machbarkeit
        navigate(`/feasibility-check?id=${requestId}`);
        break;
      case 2: // Verhandlung
        navigate(`/negotiation?id=${requestId}`);
        break;
      case 3: // Ausführung
        navigate(`/order-execution?id=${requestId}`);
        break;
      case 4: // Ergebnisse
        navigate(`/results-delivery?id=${requestId}`);
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ width: '100%', pt: 3, pb: 2 }}>
      <Typography 
        variant="h6" 
        component="div" 
        sx={{ 
          mb: 3, 
          textAlign: 'center',
          color: theme.palette.text.secondary,
          fontWeight: 500,
          letterSpacing: 0.5
        }}
      >
        {title}
      </Typography>
      
      <Box sx={{ px: 4 }}>
        <Stepper 
          activeStep={isCompleted ? steps.length : effectiveActiveStep} 
          alternativeLabel 
          connector={<CustomStepConnector />}
          sx={{ 
            '& .MuiStepConnector-root': {
              top: 20, // Zentriert die Linie mit den kleineren Icons
            }
          }}
        >
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            const isStepActive = index === effectiveActiveStep;
            const isStepCompleted = isCompleted || index < effectiveActiveStep;
            
            return (
              <Step key={label} {...stepProps}>
                <CustomStepLabel 
                  StepIconComponent={(props) => (
                    <StepIconComponent 
                      {...props} 
                      onClick={() => (isStepActive || isStepCompleted) && handleStepClick(index)}
                    />
                  )}
                  {...labelProps}
                >
                  {label}
                </CustomStepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Box>
  );
};

export default ProcessStepper;
